import { api, queryKeys, type Session } from '@alpha/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

/**
 * The session is derived from the httpOnly cookie by asking the server — the
 * SPA can't read the cookie, and deliberately so. A 401 here means "not logged
 * in", which the api-client has already tried to resolve via one refresh.
 */
export function useSession() {
  const query = useQuery({
    queryKey: queryKeys.session,
    queryFn: () => api.auth.me().then((r) => r.data),
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    session: query.data as Session | undefined,
    user: query.data?.user,
    isLoading: query.isLoading,
    isAuthenticated: !!query.data,
    isImpersonating: query.data?.is_impersonating ?? false,
    error: query.error,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => api.auth.logout(),
    onSettled: () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });
}
